import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  CircularProgress,
  Autocomplete,
  Chip,
  Alert
} from '@mui/material';
import { createContact, updateContact, getContactTags } from '../../services/contactService';

const ContactForm = React.memo(({ open, contact, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    tags: '',
    notes: ''
  });
  const [tagOptions, setTagOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      if (open && tagOptions.length === 0) {
        setLoadingTags(true);
        try {
          const tags = await getContactTags();
          setTagOptions(tags); // ✅
        } catch (error) {
          console.error('Error fetching tags:', error);
          setTagOptions(['client', 'supplier', 'vip', 'lead', 'partner', 'inactive']);
        } finally {
          setLoadingTags(false);
        }
      }
    };

    fetchTags();
  }, [open, tagOptions.length]);

  // Reset form when dialog opens/closes or contact changes
  useEffect(() => {
    if (open) {
      if (contact) {
        const tagsArray = contact.tags
            ? contact.tags.split(',').map(tag => tag.trim()).filter(Boolean)
            : [];

        setFormData({
          name: contact.name || '',
          phone: contact.phone || '',
          email: contact.email || '',
          tags: contact.tags || '',
          notes: contact.notes || ''
        });

        setSelectedTags(tagsArray);
      } else {
        setFormData({
          name: '',
          phone: '',
          email: '',
          tags: '',
          notes: ''
        });
        setSelectedTags([]);
      }
      setErrors({});
    }
  }, [open, contact]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleTagChange = useCallback((event, newValue) => {
    setSelectedTags(newValue);
    const tagsString = newValue.join(', ');
    setFormData(prev => ({ ...prev, tags: tagsString }));

    if (errors.tags) {
      setErrors(prev => ({ ...prev, tags: '' }));
    }
  }, [errors.tags]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[0-9\s-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    const tagsString = selectedTags.join(', ');
    const updatedFormData = {
      ...formData,
      tags: tagsString
    };

    setLoading(true);
    try {
      if (contact) {
        await updateContact(contact.id, updatedFormData);
      } else {
        await createContact(updatedFormData);
      }

      onClose(true);
    } catch (error) {
      console.error('Error saving contact:', error);
      if (error.response?.data?.errors) {
        const apiErrors = {};
        error.response.data.errors.forEach(err => {
          apiErrors[err.param] = err.msg;
        });
        setErrors(apiErrors);
      } else {
        setErrors({ submit: error.message || 'Failed to save contact' });
      }
    } finally {
      setLoading(false);
    }
  }, [validateForm, selectedTags, formData, contact, onClose]);

  const handleClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const dialogTitle = contact ? 'Edit Contact' : 'Add New Contact';
  const submitButtonText = loading ? 'Saving...' : 'Save';

  return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                  name="name"
                  label="Name"
                  fullWidth
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  disabled={loading}
                  variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                  name="phone"
                  label="Phone Number"
                  fullWidth
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                  disabled={loading}
                  placeholder="+1 (123) 456-7890"
                  variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading}
                  variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                  multiple
                  loading={loadingTags}
                  options={tagOptions}
                  value={selectedTags}
                  onChange={handleTagChange}
                  freeSolo
                  renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                          <Chip
                              label={option}
                              {...getTagProps({ index })}
                              size="small"
                              key={index}
                          />
                      ))
                  }
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          name="tags"
                          label="Tags"
                          placeholder="Add tags"
                          helperText="Type and press Enter to add custom tags"
                          error={!!errors.tags}
                          disabled={loading}
                          variant="outlined"
                      />
                  )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                  name="notes"
                  label="Notes"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleChange}
                  disabled={loading}
                  variant="outlined"
              />
            </Grid>
            {errors.submit && (
                <Grid item xs={12}>
                  <Alert severity="error">{errors.submit}</Alert>
                </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {submitButtonText}
          </Button>
        </DialogActions>
      </Dialog>
  );
});

ContactForm.displayName = 'ContactForm';

export default ContactForm;